import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    FolderKanban,
    Plus,
    Search,
    Filter,
    Users,
    Calendar,
    Target,
    TrendingUp,
    Clock,
    AlertCircle,
    CheckCircle,
    MoreVertical,
    Edit,
    Trash2,
    Eye,
    Download,
    Share2,
    BarChart3,
    ChevronRight
} from "lucide-react";
import BackButton from "../../components/BackButton";
import projectImg from "../../imgs/projects.svg";

const ManageProjects = () => {
    const [projects, setProjects] = useState([
        {
            id: 1,
            name: "Website Redesign",
            description: "Complete overhaul of company website with modern design",
            status: "active",
            progress: 85,
            deadline: "Dec 15, 2024",
            team: "Design Team",
            budget: "$25,000",
            members: 8,
            priority: "high",
            tasks: { completed: 24, total: 32 }
        },
        {
            id: 2,
            name: "Mobile App V2",
            description: "Development of next-generation mobile application",
            status: "active",
            progress: 60,
            deadline: "Jan 10, 2025",
            team: "Dev Team",
            budget: "$50,000",
            members: 12,
            priority: "medium",
            tasks: { completed: 18, total: 42 }
        },
        {
            id: 3,
            name: "API Integration",
            description: "Third-party API integration for payment processing",
            status: "active",
            progress: 95,
            deadline: "Nov 30, 2024",
            team: "Backend Team",
            budget: "$15,000",
            members: 4,
            priority: "high",
            tasks: { completed: 19, total: 20 }
        },
        {
            id: 4,
            name: "Analytics Dashboard",
            description: "Real-time analytics and reporting dashboard",
            status: "planning",
            progress: 45,
            deadline: "Dec 20, 2024",
            team: "Data Team",
            budget: "$30,000",
            members: 6,
            priority: "medium",
            tasks: { completed: 9, total: 24 }
        },
        {
            id: 5,
            name: "Security Audit",
            description: "Comprehensive security audit and penetration testing",
            status: "completed",
            progress: 100,
            deadline: "Oct 30, 2024",
            team: "Security Team",
            budget: "$20,000",
            members: 5,
            priority: "high",
            tasks: { completed: 15, total: 15 }
        },
        {
            id: 6,
            name: "Documentation Update",
            description: "Update all technical documentation",
            status: "on-hold",
            progress: 30,
            deadline: "Jan 15, 2025",
            team: "Tech Writing",
            budget: "$8,000",
            members: 3,
            priority: "low",
            tasks: { completed: 6, total: 24 }
        }
    ]);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [selectedPriority, setSelectedPriority] = useState("all");
    const [newProject, setNewProject] = useState({
        name: "",
        description: "",
        team: "",
        budget: "",
        deadline: ""
    });
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingProject, setEditingProject] = useState(null);

    const statusOptions = [
        { value: "all", label: "All Statuses" },
        { value: "planning", label: "Planning", color: "bg-gray-100 text-gray-800" },
        { value: "active", label: "Active", color: "bg-blue-100 text-blue-800" },
        { value: "on-hold", label: "On Hold", color: "bg-yellow-100 text-yellow-800" },
        { value: "completed", label: "Completed", color: "bg-green-100 text-green-800" }
    ];

    const priorityOptions = [
        { value: "all", label: "All Priorities" },
        { value: "high", label: "High", color: "bg-red-100 text-red-800" },
        { value: "medium", label: "Medium", color: "bg-yellow-100 text-yellow-800" },
        { value: "low", label: "Low", color: "bg-green-100 text-green-800" }
    ];

    const teams = ["Design Team", "Dev Team", "Backend Team", "Data Team", "Security Team", "Tech Writing"];

    const getStatusColor = (status) => {
        switch (status) {
            case "planning": return "bg-gray-100 text-gray-800";
            case "active": return "bg-blue-100 text-blue-800";
            case "on-hold": return "bg-yellow-100 text-yellow-800";
            case "completed": return "bg-green-100 text-green-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case "high": return "bg-red-100 text-red-800";
            case "medium": return "bg-yellow-100 text-yellow-800";
            case "low": return "bg-green-100 text-green-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const addProject = () => {
        if (newProject.name && newProject.description && newProject.team) {
            const project = {
                id: projects.length + 1,
                name: newProject.name,
                description: newProject.description,
                status: "planning",
                progress: 0,
                deadline: newProject.deadline || "TBD",
                team: newProject.team,
                budget: newProject.budget || "$0",
                members: 0,
                priority: "medium",
                tasks: { completed: 0, total: 0 }
            };
            setProjects([...projects, project]);
            setNewProject({ name: "", description: "", team: "", budget: "", deadline: "" });
            setShowAddForm(false);
        }
    };

    const updateProject = () => {
        if (editingProject && newProject.name && newProject.description && newProject.team) {
            setProjects(projects.map(p =>
                p.id === editingProject.id
                    ? { ...p, ...newProject }
                    : p
            ));
            setNewProject({ name: "", description: "", team: "", budget: "", deadline: "" });
            setEditingProject(null);
        }
    };

    const deleteProject = (id) => {
        if (window.confirm("Are you sure you want to delete this project?")) {
            setProjects(projects.filter(p => p.id !== id));
        }
    };

    const filteredProjects = projects.filter(project => {
        const matchesSearch =
            project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            project.team.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = selectedStatus === "all" || project.status === selectedStatus;
        const matchesPriority = selectedPriority === "all" || project.priority === selectedPriority;

        return matchesSearch && matchesStatus && matchesPriority;
    });

    const stats = {
        total: projects.length,
        active: projects.filter(p => p.status === "active").length,
        completed: projects.filter(p => p.status === "completed").length,
        totalBudget: projects.reduce((sum, p) => sum + parseInt(p.budget.replace(/[^0-9]/g, '')) / 100, 0),
        totalMembers: projects.reduce((sum, p) => sum + p.members, 0)
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <BackButton to="/project-leader/dashboard" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Manage Projects</h1>
                                <p className="text-gray-600 mt-1">Oversee and manage all your projects</p>
                            </div>
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setShowAddForm(true)}
                            className="btn-primary flex items-center"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            New Project
                        </motion.button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-8">
                {/* Stats Overview */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Projects</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                                </div>
                                <FolderKanban className="w-8 h-8 text-primary-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Active Projects</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                                </div>
                                <TrendingUp className="w-8 h-8 text-green-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Budget</p>
                                    <p className="text-2xl font-bold text-gray-900">${stats.totalBudget.toLocaleString()}</p>
                                </div>
                                <BarChart3 className="w-8 h-8 text-blue-600" />
                            </div>
                        </div>
                        <div className="card">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Team Members</p>
                                    <p className="text-2xl font-bold text-gray-900">{stats.totalMembers}</p>
                                </div>
                                <Users className="w-8 h-8 text-purple-600" />
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Search and Filters */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search projects..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-field pl-10"
                            />
                        </div>
                        <div className="flex gap-3">
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedPriority}
                                onChange={(e) => setSelectedPriority(e.target.value)}
                                className="input-field min-w-[150px]"
                            >
                                {priorityOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                                <Filter className="w-5 h-5 mr-2" />
                                More Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Add/Edit Form */}
                {(showAddForm || editingProject) && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card mb-8"
                    >
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">
                            {editingProject ? "Edit Project" : "Add New Project"}
                        </h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Project Name *
                                </label>
                                <input
                                    type="text"
                                    value={newProject.name}
                                    placeholder="Enter project name"
                                    onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                                    className="input-field"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Team *
                                </label>
                                <select
                                    value={newProject.team}
                                    onChange={(e) => setNewProject({...newProject, team: e.target.value})}
                                    className="input-field"
                                    required
                                >
                                    <option value="">Select team</option>
                                    {teams.map(team => (
                                        <option key={team} value={team}>{team}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description *
                                </label>
                                <textarea
                                    value={newProject.description}
                                    placeholder="Describe the project..."
                                    onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                                    className="input-field min-h-[100px] resize-none"
                                    rows={3}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Budget
                                </label>
                                <input
                                    type="text"
                                    value={newProject.budget}
                                    placeholder="$0.00"
                                    onChange={(e) => setNewProject({...newProject, budget: e.target.value})}
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Deadline
                                </label>
                                <input
                                    type="date"
                                    value={newProject.deadline}
                                    onChange={(e) => setNewProject({...newProject, deadline: e.target.value})}
                                    className="input-field"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAddForm(false);
                                    setEditingProject(null);
                                    setNewProject({ name: "", description: "", team: "", budget: "", deadline: "" });
                                }}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingProject ? updateProject : addProject}
                                className="btn-primary"
                            >
                                {editingProject ? "Update Project" : "Add Project"}
                            </button>
                        </div>
                    </motion.div>
                )}

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Projects List */}
                    <div className="lg:col-span-2">
                        <div className="space-y-4">
                            {filteredProjects.map((project, index) => (
                                <motion.div
                                    key={project.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="card hover:shadow-medium group"
                                >
                                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-start space-x-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mt-1">
                                                        <FolderKanban className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center space-x-3 mb-1">
                                                            <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                                                            <div className="flex items-center space-x-2">
                                <span className={`badge ${getStatusColor(project.status)}`}>
                                  {project.status}
                                </span>
                                                                <span className={`badge ${getPriorityColor(project.priority)}`}>
                                  {project.priority}
                                </span>
                                                            </div>
                                                        </div>
                                                        <p className="text-gray-600 text-sm mb-3">{project.description}</p>

                                                        <div className="flex flex-wrap gap-4 text-sm">
                                                            <div className="flex items-center space-x-2">
                                                                <Users className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">{project.team}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2">
                                                                <Target className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">{project.budget}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2">
                                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                                <span className="text-gray-600">{project.deadline}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingProject(project);
                                                            setNewProject({
                                                                name: project.name,
                                                                description: project.description,
                                                                team: project.team,
                                                                budget: project.budget,
                                                                deadline: project.deadline
                                                            });
                                                        }}
                                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Edit project"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteProject(project.id)}
                                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete project"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                    <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                                        <MoreVertical className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Progress Bar */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-600">Progress</span>
                                                    <span className="font-medium text-gray-900">{project.progress}%</span>
                                                </div>
                                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full ${
                                                            project.progress < 50 ? 'bg-red-500' :
                                                                project.progress < 80 ? 'bg-yellow-500' : 'bg-green-500'
                                                        }`}
                                                        style={{ width: `${project.progress}%` }}
                                                    />
                                                </div>
                                                <div className="flex justify-between text-sm text-gray-500">
                                                    <span>Tasks: {project.tasks.completed}/{project.tasks.total}</span>
                                                    <button className="text-primary-600 hover:text-primary-700 font-medium flex items-center">
                                                        View Details
                                                        <ChevronRight className="w-4 h-4 ml-1" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Empty State */}
                        {filteredProjects.length === 0 && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center py-12"
                            >
                                <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <FolderKanban className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
                                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                                    {searchTerm || selectedStatus !== 'all' || selectedPriority !== 'all'
                                        ? "No projects match your search criteria. Try adjusting your filters."
                                        : "Get started by creating your first project."
                                    }
                                </p>
                                {(searchTerm || selectedStatus !== 'all' || selectedPriority !== 'all') ? (
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setSelectedStatus('all');
                                            setSelectedPriority('all');
                                        }}
                                        className="btn-primary"
                                    >
                                        Clear Filters
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setShowAddForm(true)}
                                        className="btn-primary"
                                    >
                                        <Plus className="w-5 h-5 mr-2" />
                                        Create Your First Project
                                    </button>
                                )}
                            </motion.div>
                        )}
                    </div>

                    {/* Sidebar - Quick Actions & Stats */}
                    <div className="space-y-6">
                        {/* Project Overview */}
                        <div className="card">
                            <div className="text-center mb-6">
                                <img
                                    src={projectImg}
                                    alt="Project Management"
                                    className="w-full max-w-xs mx-auto mb-4 rounded-lg"
                                />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Project Management</h3>
                                <p className="text-gray-600 text-sm">
                                    Organize and track your projects efficiently with our management tools.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                        <span className="font-medium text-gray-700">Completion Rate</span>
                                    </div>
                                    <span className="font-medium text-gray-900">
                    {Math.round((stats.completed / stats.total) * 100)}%
                  </span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <Clock className="w-5 h-5 text-yellow-600" />
                                        <span className="font-medium text-gray-700">Avg. Timeline</span>
                                    </div>
                                    <span className="font-medium text-gray-900">45 days</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <AlertCircle className="w-5 h-5 text-red-600" />
                                        <span className="font-medium text-gray-700">At Risk</span>
                                    </div>
                                    <span className="font-medium text-gray-900">2 projects</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                            <div className="space-y-2">
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <Download className="w-5 h-5" />
                                        <span className="font-medium">Export Projects</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <Share2 className="w-5 h-5" />
                                        <span className="font-medium">Share Reports</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                                <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors group">
                                    <div className="flex items-center space-x-3">
                                        <Eye className="w-5 h-5" />
                                        <span className="font-medium">View Analytics</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                            </div>
                        </div>

                        {/* Status Breakdown */}
                        <div className="card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Status</h3>
                            <div className="space-y-3">
                                {statusOptions.filter(s => s.value !== 'all').map(status => (
                                    <div key={status.value} className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <div className={`w-3 h-3 rounded-full ${status.color.split(' ')[0]}`} />
                                            <span className="text-sm text-gray-600">{status.label}</span>
                                        </div>
                                        <span className="font-medium text-gray-900">
                      {projects.filter(p => p.status === status.value).length}
                    </span>
                                    </div>
                                ))}
                                <div className="pt-3">
                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-blue-500 via-green-500 to-yellow-500 rounded-full" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageProjects;